import React, { useMemo } from 'react';
import type { ViewsService } from '@/engine/app/Views.service.ts';
import type { BasicBoxProps, MeasurementProps } from '../types.ts';
import type { MeasurementBoxesController } from '../index.ts';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';

import { TYPES } from '@/engine/ioc';

import { useInjection } from 'inversify-react';
import { useHighlight } from '../../hooks/useHighlight.ts';
import { useSelect } from '../../hooks/useSelect.ts';
import { observer } from 'mobx-react';
import { DistanceMeasurementRow } from '@/components/boxes/measurementBoxes/DistanceMeasurementBox/DistanceMeasurementRow.tsx';
import { ButtonsContainer, Container } from '@/components/boxes/box-containers/boxContainers';
import { Tooltip } from '@mui/material';
import { PenCheckboxRender } from '@/components/common/expandableTable/PenCheckboxRender';
import { EyeCheckboxRender } from '@/components/common/expandableTable/EyeCheckboxRender';

interface DistanceMeasurementBoxProps extends BasicBoxProps<MeasurementProps> {
  hasCriteria: boolean | undefined;
}

export const DistanceMeasurementBox: React.FC<DistanceMeasurementBoxProps> = observer(
  ({ measurements, hasCriteria }: DistanceMeasurementBoxProps) => {
    const mainController = useInjection<MeasurementBoxesController>(TYPES.IMeasurementsBoxesController);
    const viewsService = useInjection<ViewsService>(TYPES.ViewsService);
    const measurementVMs = viewsService.currentView?.getMeasurements();
    const allDescriptors = useMemo(() => measurements.map((m) => m.descriptor), [measurements]);
    const { isHighlighted } = useHighlight(measurementVMs);
    const { isSelected } = useSelect(measurementVMs);

    return (
      <Container>
        <ButtonsContainer>
          <Tooltip title="Toggle edit mode" placement="top">
            <span>
              <PenCheckboxRender
                onChange={(checked: boolean): void =>
                  mainController.updateAllViewItemsBeingEdited(allDescriptors, checked)
                }
                isChecked={mainController.checkGroupViewItemsBeingEdited(allDescriptors)}
                isEmptyBox={measurements.length === 0}
              />
            </span>
          </Tooltip>
          <Tooltip title="Toggle visibility" placement="top">
            <span>
              <EyeCheckboxRender
                onChange={(isVisible: boolean): void => {
                  mainController.updateAllViewItemsVisibility(allDescriptors, isVisible);
                }}
                isChecked={mainController.checkGroupViewItemsVisibility(allDescriptors)}
                isEmptyBox={measurements.length === 0}
              />
            </span>
          </Tooltip>
        </ButtonsContainer>
        <TableContainer component={Paper}>
          <Table aria-label="a dense table" size="small">
            <TableBody>
              {measurements.map((measurement: MeasurementProps, index: number) => {
                return (
                  <DistanceMeasurementRow
                    key={index}
                    measurement={measurement}
                    mainController={mainController}
                    isSelected={isSelected}
                    isHighlighted={isHighlighted}
                    hasCriteria={hasCriteria}
                    index={index}
                  />
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>
    );
  }
);
